import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Home.css';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Hero carousel data
  const heroSlides = [
    {
      id: 1,
      title: 'CloudeyStyles.',
      subtitle: 'Discover our wide ranging and timeless of lifestyle products. Pick your favourite stuff that matches your personal taste, style and suits your style.',
      image: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=1600&h=900&fit=crop',
      buttonText: 'Start shopping'
    },
    {
      id: 2,
      title: 'New Arrivals.',
      subtitle: 'Explore the latest trends in fashion. Fresh styles that define your unique personality.',
      image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=1600&h=900&fit=crop',
      buttonText: 'Explore Now'
    },
    {
      id: 3,
      title: 'Summer Collection.',
      subtitle: 'Beat the heat with our cool summer styles. Comfortable, trendy, and affordable.',
      image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1600&h=900&fit=crop',
      buttonText: 'Shop Summer'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      const productsRes = await productsAPI.getAll({ limit: 10 });
      setFeaturedProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success('Added to cart!');
  };

  const getImageUrl = (product) => {
    const validImages = (product.images || []).filter(img => img && img !== null && img !== '');
    const firstImage = validImages[0];
    if (!firstImage) return null;
    
    // Construct proper image URL
    if (firstImage.startsWith('http://') || firstImage.startsWith('https://')) {
      return firstImage;
    } else if (firstImage.startsWith('/uploads/')) {
      return `http://localhost:5000${firstImage}`;
    } else {
      return `http://localhost:5000/uploads/${firstImage}`;
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Carousel Section */}
      <section className="hero-carousel">
        <div className="carousel-container">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="carousel-overlay"></div>
              <div className="carousel-content">
                <h1 className="carousel-title">{slide.title}</h1>
                <p className="carousel-subtitle">{slide.subtitle}</p>
                <Link to="/products" className="carousel-btn">
                  {slide.buttonText}
                  <FiArrowRight size={20} />
                </Link>
              </div>
            </div>
          ))}
          
          <button className="carousel-nav prev" onClick={prevSlide} aria-label="Previous slide">
            <FiChevronLeft size={28} />
          </button>
          <button className="carousel-nav next" onClick={nextSlide} aria-label="Next slide">
            <FiChevronRight size={28} />
          </button>
          
          <div className="carousel-indicators">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Collections Section */}
      <section className="collections-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Styles That Welcome Sunshine's Return</h2>
            <p>Recently released the products are light, colourful, comfortable, and making them perfect for enjoying the outdoors in pleasant weather. It'll suitable for the warm, sunny days of spring or summer.</p>
          </div>
          
          <div className="collections-grid">
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1552374196-1ab2a1c593e8?w=600&h=800&fit=crop" alt="Men's Collection" />
              </div>
              <div className="collection-overlay">
                <h3>Men's Collection</h3>
                <Link to="/products?gender=men" className="collection-link">Shop Now</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=800&fit=crop" alt="Casual Wear" />
              </div>
              <div className="collection-overlay">
                <h3>Casual Wear</h3>
                <Link to="/products?category=casual" className="collection-link">Explore</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1487222477894-8943e31ef7b2?w=600&h=800&fit=crop" alt="Women's Fashion" />
              </div>
              <div className="collection-overlay">
                <h3>Women's Fashion</h3>
                <Link to="/products?gender=women" className="collection-link">Shop Now</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1618453292610-0b8ffb7f29ab?w=600&h=800&fit=crop" alt="Street Style" />
              </div>
              <div className="collection-overlay">
                <h3>Street Style</h3>
                <Link to="/products?category=streetwear" className="collection-link">Explore</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1496217590455-aa63a8350eea?w=600&h=800&fit=crop" alt="Accessories" />
              </div>
              <div className="collection-overlay">
                <h3>Accessories</h3>
                <Link to="/products?category=accessories" className="collection-link">Shop Now</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600&h=800&fit=crop" alt="Shoes Collection" />
              </div>
              <div className="collection-overlay">
                <h3>Shoes Collection</h3>
                <Link to="/products?category=shoes" className="collection-link">Shop Now</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600&h=800&fit=crop" alt="Sportswear" />
              </div>
              <div className="collection-overlay">
                <h3>Sportswear</h3>
                <Link to="/products?category=sportswear" className="collection-link">Explore</Link>
              </div>
            </div>
            
            <div className="collection-card">
              <div className="collection-image">
                <img src="https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=600&h=800&fit=crop" alt="Outerwear" />
              </div>
              <div className="collection-overlay">
                <h3>Outerwear</h3>
                <Link to="/products?category=outerwear" className="collection-link">Shop Now</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="blog-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Read Super Blog.</h2>
            <p>Dive into a sea of stylish outfits that promise to impress and delight with their cool and trendy selections.</p>
          </div>
          
          <div className="blog-grid">
            <div className="blog-card">
              <div className="blog-image">
                <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&h=600&fit=crop" alt="Fashion Blog" />
              </div>
              <div className="blog-content">
                <h3>New Fashion Arrival, Introducing The Skate Fashion 2024.</h3>
                <p>Dive into the world of everlasting elegance and fashion finesse with Chic Chronicles Skate Fashion Outfit that will arrive in 2024...</p>
                <Link to="/blogs/skate-fashion-2024" className="blog-link">See More</Link>
              </div>
            </div>
            
            <div className="blog-card">
              <div className="blog-image">
                <img src="https://images.unsplash.com/photo-1445205170230-053b83016050?w=800&h=600&fit=crop" alt="Wardrobe Blog" />
              </div>
              <div className="blog-content">
                <h3>Wanderlust Wardrobe: Fashion from Every Corner of the Globe</h3>
                <p>Embark on a sartorial journey around the world with Wanderlust Wardrobe. Skate Fashion Outfit that will arrive in 2024...</p>
                <Link to="/blogs/wanderlust-wardrobe" className="blog-link">See More</Link>
              </div>
            </div>
          </div>
          
          <div className="blog-view-more">
            <Link to="/blogs" className="view-more-btn">View more</Link>
          </div>
        </div>
      </section>



      {/* Featured Products Section */}
      {featuredProducts.length > 0 && (
        <section className="featured-products-section">
          <div className="section-container">
            <div className="section-header">
              <h2>Featured Products</h2>
              <p>Handpicked selections just for you</p>
            </div>
            
            <div className="products-grid-new">
              {featuredProducts.map((product) => (
                <Link
                  key={product._id}
                  to={`/products/${product._id}`}
                  className="product-card-new"
                >
                  <div className="product-image-new">
                    {getImageUrl(product) ? (
                      <img
                        src={getImageUrl(product)}
                        alt={product.name}
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error('Home image error:', getImageUrl(product));
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('✅ Home loaded:', getImageUrl(product))}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f3f4f6',
                        color: '#9ca3af',
                        fontSize: '14px'
                      }}>
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="#9ca3af"/>
                        </svg>
                        <span style={{ marginTop: '8px' }}>No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="product-info-new">
                    <h3>{product.name}</h3>
                    <p className="product-price-new">${product.price.toFixed(2)}</p>
                    <div className="product-stats">
                      <span className="stock">Stock: {product.stock || 0}</span>
                      <span className="sold">Sold: {product.sold || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="section-footer">
              <Link to="/products" className="view-all-btn">
                View All Products
                <FiArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default Home;